import type { PickerSource } from 'slices/aiConversations/pickerTypes'

import { errorHandler } from 'utilities/errorHandler'
import type {
  PickerRowKind,
  PickerRowsByKeyArgs,
} from '../canonicalPicker.types'

import { normalizeEndpoint } from '../pickerData'
import { isSourceFilter, isString, stringsOf } from './guards'

export const PAGE_SIZE = 50
// The most rows snowflake/query returns in one page
export const MAX_PAGE_SIZE = 1000
// Uids looked up per request, so the query string stays under the server's 10 KB
export const UIDS_PER_REQUEST = 20
// Keys looked up per request, so 36-character uid groups stay under the same 10 KB
export const KEYS_PER_REQUEST = 50
// Products whose groups are read per request; each adds a condition, so fewer fit
export const PRODUCTS_PER_REQUEST = 20

// A source param value that stands for the canonical ids picked one level up
const SCOPE_PLACEHOLDER = ':level_1'

// What the UI reads off a te_canonical_provider_uid_groups row, beyond a source's key and value
export const UID_GROUP_FIELD = {
  provider: 'provider',
  canonicalId: 'canonical_product_id',
  canonicalName: 'canonical_product_name',
  locationIds: 'location_ids',
  // Non-empty only on a group keyed by its provider's global uid
  globalUids: 'provider_global_uids',
  storeUids: 'provider_uids',
  priceMin: 'price_cents_min',
  priceMax: 'price_cents_max',
  // Every canonical product the group's variants belong to (thanx-dbt#1168)
  canonicalIds: 'canonical_product_ids',
}

// What a list of groups reads; a lookup by key or uid also reads prices and every product
const LISTED_FIELDS = [
  UID_GROUP_FIELD.provider,
  UID_GROUP_FIELD.canonicalId,
  UID_GROUP_FIELD.canonicalName,
  UID_GROUP_FIELD.locationIds,
  UID_GROUP_FIELD.globalUids,
  UID_GROUP_FIELD.storeUids,
]
const DETAIL_FIELDS = [
  UID_GROUP_FIELD.priceMin,
  UID_GROUP_FIELD.priceMax,
  UID_GROUP_FIELD.canonicalIds,
]

const reportedMissing = new Set<string>()

/** Says once per source and column that thanx-ai's config left out a column the UI reads */
const reportMissing = (source: PickerSource, fields: string[]) => {
  const unreported = fields.filter(
    field => !reportedMissing.has(`${source.name}:${field}`)
  )
  if (unreported.length === 0) return
  unreported.forEach(field => reportedMissing.add(`${source.name}:${field}`))
  errorHandler.capture(
    'Canonical picker source omits fields the picker reads; requesting them anyway',
    { source: source.name, table: source.params.table_name, fields: unreported }
  )
}

/**
 * The columns a fixed list asks for: those thanx-ai's config lists for the
 * source, plus any the UI reads that an older config leaves out.
 */
export const uidGroupFieldsOf = (
  source: PickerSource,
  needed: string[]
): string[] => {
  const configured = stringsOf(source.params.fields)
  const required = [source.keyField, source.valueField, ...needed]
  const missing = required.filter(field => !configured.includes(field))
  if (missing.length > 0) reportMissing(source, missing)
  return Array.from(new Set([...configured, ...missing]))
}

type Operator = '=' | 'like' | 'in' | 'array_contains'

interface Condition {
  field: string
  operator: Operator
  value: string | string[]
}

interface ConditionGroup {
  logicalOperator: 'AND' | 'OR'
  conditions: Condition[]
}

interface SnowflakeQuery {
  fields: string[]
  distinct: boolean
  where: Array<Condition | ConditionGroup>
  orderBy: string[]
  page: number
  perPage: number
}

/**
 * Each top-level condition goes out as a group of its own. Rack starts a new
 * `where[]` entry only when a key repeats, so a plain condition next to a
 * group would otherwise be merged into it.
 */
const appendWhere = (
  params: URLSearchParams,
  clause: Condition | ConditionGroup
) => {
  const group =
    'conditions' in clause
      ? clause
      : { logicalOperator: 'AND', conditions: [clause] }
  params.append('where[][logical_operator]', group.logicalOperator)
  group.conditions.forEach(({ field, operator, value }) => {
    params.append('where[][conditions][][field]', field)
    params.append('where[][conditions][][operator]', operator)
    if (Array.isArray(value)) {
      value.forEach(item =>
        params.append('where[][conditions][][value][]', item)
      )
    } else {
      params.append('where[][conditions][][value]', value)
    }
  })
}

export const snowflakeQueryString = (
  tableName: string,
  query: SnowflakeQuery
): string => {
  const params = new URLSearchParams()
  params.append('table_name', tableName)
  params.append('distinct', String(query.distinct))
  query.fields.forEach(field => params.append('fields[]', field))
  query.where.forEach(clause => appendWhere(params, clause))
  query.orderBy.forEach(field => {
    params.append('order_by[][field]', field)
    params.append('order_by[][direction]', 'asc')
  })
  params.append('page', String(query.page))
  params.append('per_page', String(query.perPage))
  return params.toString()
}

export const filterConditions = (source: PickerSource): Condition[] =>
  (Array.isArray(source.params.filters) ? source.params.filters : [])
    .filter(isSourceFilter)
    .map(({ field, value }) => ({ field, operator: '=', value }))

/** Whether a source's rows are filtered to the canonical ids picked one level up */
export const isScopedSource = (source: PickerSource): boolean =>
  isString(source.params.filter_field) &&
  source.params.filter_value === SCOPE_PLACEHOLDER

export const scopeConditions = (
  source: PickerSource,
  scope: string[] | null
): Condition[] => {
  const { filter_field: field } = source.params
  if (!isScopedSource(source) || !isString(field) || scope === null) return []
  return [{ field, operator: 'in', value: scope }]
}

/**
 * The source's search columns, ORed. A name matches any part of the term; an
 * id, the row's key or an element of an array column, only the whole of it.
 */
export const searchConditions = (
  source: PickerSource,
  search: string
): ConditionGroup[] => {
  const term = search.trim()
  if (!term) return []

  const { search_field: searchField } = source.params
  const fields = stringsOf(source.params.search_fields)
  const arrayFields = stringsOf(source.params.array_search_fields)
  const searched = fields.length > 0 ? fields : stringsOf([searchField])
  if (searched.length === 0) return []

  const conditionFor = (field: string): Condition => {
    if (arrayFields.includes(field)) {
      return { field, operator: 'array_contains', value: term }
    }
    if (field === source.keyField) return { field, operator: '=', value: term }
    // A % or _ the merchant types stays a wildcard: nexus's ILIKE has no ESCAPE clause to honour an escaped one
    return { field, operator: 'like', value: `%${term}%` }
  }
  return [{ logicalOperator: 'OR', conditions: searched.map(conditionFor) }]
}

export const fieldsFor = (
  source: PickerSource,
  kind: PickerRowKind,
  withDetails: boolean
): string[] => {
  if (kind === 'product') {
    const variants =
      withDetails && source.variantsField ? [source.variantsField] : []
    return Array.from(
      new Set([source.keyField, source.valueField, ...variants])
    )
  }
  return uidGroupFieldsOf(
    source,
    withDetails ? [...LISTED_FIELDS, ...DETAIL_FIELDS] : LISTED_FIELDS
  )
}

export const orderFor = (source: PickerSource, kind: PickerRowKind): string[] =>
  kind === 'product'
    ? [source.valueField, source.keyField]
    : [source.valueField, UID_GROUP_FIELD.provider, source.keyField]

export const queryUrl = (
  source: PickerSource,
  query: SnowflakeQuery
): string => {
  const tableName = source.params.table_name
  return `${normalizeEndpoint(source.endpoint)}?${snowflakeQueryString(
    isString(tableName) ? tableName : '',
    query
  )}`
}

/** The groups that have one of the uids, exactly, as their group or a store uid */
const uidConditions = (
  source: PickerSource,
  uids: string[]
): ConditionGroup => ({
  logicalOperator: 'OR',
  conditions: [
    { field: source.keyField, operator: 'in', value: uids },
    ...uids.map(
      (uid): Condition => ({
        field: UID_GROUP_FIELD.storeUids,
        operator: 'array_contains',
        value: uid,
      })
    ),
  ],
})

/** The groups that list one of the uids among their store UIDs, whatever locations the merchant can see */
export const groupsHoldingUrl = (
  source: PickerSource,
  uids: string[],
  page: number
): string =>
  queryUrl(source, {
    fields: fieldsFor(source, 'uidGroup', true),
    distinct: source.params.distinct === true,
    where: [
      ...filterConditions(source),
      {
        logicalOperator: 'OR',
        conditions: uids.map(uid => ({
          field: UID_GROUP_FIELD.storeUids,
          operator: 'array_contains',
          value: uid,
        })),
      },
    ],
    orderBy: orderFor(source, 'uidGroup'),
    page,
    perPage: MAX_PAGE_SIZE,
  })

// No scope, like a search of the specific source: it names no :level_1 filter, as uid groups aren't filed under one item
export const uidLookupUrl = (
  source: PickerSource,
  uids: string[],
  page = 1
): string =>
  queryUrl(source, {
    fields: fieldsFor(source, 'uidGroup', true),
    distinct: source.params.distinct === true,
    where: [...filterConditions(source), uidConditions(source, uids)],
    orderBy: orderFor(source, 'uidGroup'),
    page,
    perPage: MAX_PAGE_SIZE,
  })

export const chunksOf = <T>(items: T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(items.length / size) }, (_, index) =>
    items.slice(index * size, (index + 1) * size)
  )

const isEmptyIn = ({ operator, value }: Condition): boolean =>
  operator === 'in' && value.length === 0

/**
 * The queries that read every row of the given keys, by page. A scoped source
 * holds a row per key and scoped id, so it asks for fewer keys at a time. The
 * server reads an empty `in` as no filter at all, so nothing is asked for then.
 */
export const rowsByKeyUrls = ({
  source,
  kind,
  keys,
  scope,
}: PickerRowsByKeyArgs): Array<(page: number) => string> => {
  const scopeFilter = scopeConditions(source, scope)
  if (keys.length === 0 || scopeFilter.some(isEmptyIn)) return []

  const rowsPerKey = scopeFilter.length > 0 && scope ? scope.length : 1
  const keysPerRequest = Math.max(
    1,
    Math.min(KEYS_PER_REQUEST, Math.floor(MAX_PAGE_SIZE / rowsPerKey))
  )
  return chunksOf(keys, keysPerRequest).map(
    chunk => page =>
      queryUrl(source, {
        fields: fieldsFor(source, kind, true),
        distinct: source.params.distinct === true,
        where: [
          ...filterConditions(source),
          ...scopeFilter,
          { field: source.keyField, operator: 'in', value: chunk },
        ],
        orderBy: [source.keyField],
        page,
        perPage: MAX_PAGE_SIZE,
      })
  )
}

/** The groups any of the products' variants belong to, with their store uids but no prices */
export const groupsOfProductsUrl = (
  source: PickerSource,
  productIds: string[],
  page: number
): string =>
  queryUrl(source, {
    fields: uidGroupFieldsOf(source, [
      ...LISTED_FIELDS,
      UID_GROUP_FIELD.canonicalIds,
    ]),
    distinct: source.params.distinct === true,
    where: [
      ...filterConditions(source),
      {
        logicalOperator: 'OR',
        conditions: productIds.map(productId => ({
          field: UID_GROUP_FIELD.canonicalIds,
          operator: 'array_contains',
          value: productId,
        })),
      },
    ],
    orderBy: orderFor(source, 'uidGroup'),
    page,
    perPage: MAX_PAGE_SIZE,
  })

// What a store UID row is read by and shows, as its details source names the columns
export const STORE_UID_COLUMNS = {
  provider: 'provider',
  uid: 'provider_uid',
  name: 'provider_name',
  locationIds: 'location_ids',
  canonicalId: 'canonical_product_id',
  canonicalName: 'canonical_product_name',
}

/** Whether a source names where its UID groups' store UIDs are read, by provider and uid */
export const canListStoreUids = (source: PickerSource): boolean => {
  const fields = source.detailsSource?.fields ?? []
  return (
    fields.includes(STORE_UID_COLUMNS.provider) &&
    fields.includes(STORE_UID_COLUMNS.uid)
  )
}

/**
 * The queries that read a group's store UIDs from the details source, by page,
 * asking only for the columns it names. The source's own filters apply when the
 * details source has their column, so an item's UIDs never list a modifier's.
 */
export const storeUidsUrls = (
  source: PickerSource,
  /** null reads the uids under any provider */
  provider: string | null,
  uids: string[]
): Array<(page: number) => string> => {
  const details = source.detailsSource
  if (!details || !canListStoreUids(source) || uids.length === 0) return []
  const fields = Object.values(STORE_UID_COLUMNS).filter(field =>
    details.fields.includes(field)
  )
  const filters = filterConditions(source).filter(({ field }) =>
    details.fields.includes(field)
  )
  return chunksOf(uids, KEYS_PER_REQUEST).map(
    chunk => page =>
      `${normalizeEndpoint(source.endpoint)}?${snowflakeQueryString(
        details.tableName,
        {
          fields,
          distinct: true,
          where: [
            ...filters,
            ...(provider === null
              ? []
              : [
                  {
                    field: STORE_UID_COLUMNS.provider,
                    operator: '=' as const,
                    value: provider,
                  },
                ]),
            { field: STORE_UID_COLUMNS.uid, operator: 'in', value: chunk },
          ],
          orderBy: [STORE_UID_COLUMNS.uid],
          page,
          perPage: MAX_PAGE_SIZE,
        }
      )}`
  )
}

/**
 * The keys a scoped source lists under the given canonical ids, by page: the
 * products a level 2 holds under level 1's picks.
 */
export const scopedKeysUrls = (
  source: PickerSource,
  scope: string[]
): Array<(page: number) => string> =>
  isScopedSource(source)
    ? chunksOf(scope, KEYS_PER_REQUEST).map(
        chunk => page =>
          queryUrl(source, {
            fields: [source.keyField],
            distinct: true,
            where: [
              ...filterConditions(source),
              ...scopeConditions(source, chunk),
            ],
            orderBy: [source.keyField],
            page,
            perPage: MAX_PAGE_SIZE,
          })
      )
    : []
